import React, { useEffect, useState } from "react";

/**
 * HeroVideo
 *
 * Reusable cinematic brand-tone hero. Currently re-uses the /business hero
 * video as a TEMPORARY placeholder across every landing page; each page will
 * eventually swap in category-specific footage (life-legacy reel, atelier
 * print-room reel, etc.). Behavior mirrors the /business hero exactly:
 *
 *   - Cloudinary-served, responsive desktop + mobile sources
 *   - matchMedia('(max-width: 767px)') decides which source loads; the other
 *     is never downloaded
 *   - autoplay, muted, loop, playsInline, preload="metadata"
 *   - Two layered scrims (warm directional + bottom floor) so overlay text
 *     stays readable without darkening the footage
 *   - Children render as the overlay (kicker / headline / subhead / CTAs)
 *
 * Each page passes in its OWN copy via `children` — no shared overlay
 * content. This component only owns the visual treatment.
 */

const VIDEO_DESKTOP =
    "https://res.cloudinary.com/dub8rhyez/video/upload/f_auto,q_auto,vc_auto,w_1920,c_limit/vonstuben_business_nnxo7a.mp4";
const POSTER_DESKTOP =
    "https://res.cloudinary.com/dub8rhyez/video/upload/so_0,w_1280,c_fill,ar_16:9,q_auto,f_jpg/vonstuben_business_nnxo7a.jpg";
const VIDEO_MOBILE =
    "https://res.cloudinary.com/dub8rhyez/video/upload/f_auto,q_auto,vc_auto,w_1080,c_limit/vonstuben_mobile_vb6ru1.mp4";
const POSTER_MOBILE =
    "https://res.cloudinary.com/dub8rhyez/video/upload/so_0,w_1080,c_fill,ar_9:16,q_auto,f_jpg/vonstuben_mobile_vb6ru1.jpg";

export function HeroVideo({
    children,
    // Default mirrors /business proportions. Pages that want a shorter hero
    // (utility pages like /inquire, /journal) can pass `height="short"`.
    height = "tall",
    objectPositionDesktop = "62% 50%",
    objectPositionMobile = "50% 38%",
    ariaLabel = "Editorial photography and visual storytelling by Elizaveta von Stuben Imagery.",
    dataTestId = "hero-video",
    // "end" anchors overlay to the bottom (cinematic / studio pages);
    // "center" vertically centers it (utility pages, lighter feel).
    align = "end",
    children2,
    // Phase 17 — optional Sanity-driven hero media. When provided (and
    // shape includes desktop/mobile URLs), overrides the hardcoded
    // VIDEO_DESKTOP/VIDEO_MOBILE constants. Auto-detects image vs video
    // from the Cloudinary URL path (/video/upload/ vs /image/upload/)
    // and renders <video> or <img> accordingly. When null/undefined
    // (Sanity doc missing or unpublished), falls back to the existing
    // business placeholder so the page never breaks.
    media = null,
}) {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mql = window.matchMedia("(max-width: 767px)");
        const apply = () => setIsMobile(mql.matches);
        apply();
        if (mql.addEventListener) mql.addEventListener("change", apply);
        else mql.addListener(apply);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener("change", apply);
            else mql.removeListener(apply);
        };
    }, []);

    // Phase 17 — Sanity media override + auto-detect. heroMedia docs can
    // carry either an image (resource_type='image' → /image/upload/ URLs)
    // or a video (resource_type='video' → /video/upload/ URLs). Detect by
    // URL path so the same prop shape works for both. Falls back to the
    // hardcoded business placeholder when no override is supplied.
    const overrideDesktop = media?.desktop || null;
    const overrideMobile = media?.mobile || media?.desktop || null;
    const overridePoster = media?.poster || media?.posterMobile || null;
    const overrideUrl = isMobile ? overrideMobile : overrideDesktop;
    const overrideIsVideo =
        !!overrideUrl &&
        (media?.mediaType === 'video' || /\/video\/upload\//.test(overrideUrl));
    const src = overrideUrl && overrideIsVideo
        ? overrideUrl
        : (isMobile ? VIDEO_MOBILE : VIDEO_DESKTOP);
    const imageSrc = overrideUrl && !overrideIsVideo ? overrideUrl : null;
    const poster = overridePoster || (isMobile ? POSTER_MOBILE : POSTER_DESKTOP);
    const objectPosition = isMobile ? objectPositionMobile : objectPositionDesktop;

    // Height: explicit `h-[88svh]` (not min-h-) on mobile too. svh =
    // "small viewport height" (stable: anchored to the smallest possible
    // viewport state in Safari, doesn't jump as the address bar
    // shrinks/grows). Pairing it with an explicit `h-` is required so
    // the inner flex's `h-full` resolves to a definite parent height —
    // otherwise `justify-end` is a no-op on mobile. `min-h-` floors
    // guard older browsers without svh support and absurdly small
    // window heights.
    const heightClasses =
        height === "short"
            ? "h-[68svh] min-h-[440px] sm:min-h-[480px]"
            : "h-[88svh] min-h-[560px] sm:min-h-[600px]";

    // HOTFIX (Phase 11.3 — editorial bottom-anchor + hard nav-clearance
    // floor, take 3):
    //
    // Background. Phase 11.1 used `justify-end pb-10 pt-20`. Two bugs
    // combined: (a) the section was `min-h-[88svh]` (not `h-`), so the
    // inner flex's `h-full` collapsed and `justify-end` did nothing,
    // letting content stack from the top in document order; (b) on
    // extreme-short viewports the dense content overflowed upward past
    // the pt-20 edge into the fixed nav. Phase 11.2 switched mobile to
    // `justify-start pt-24 pb-8` which fixed the nav collision but
    // over-corrected — the editorial bottom-anchored feel was lost.
    //
    // This take rebuilds the overlay as a three-layer flex so BOTH
    // constraints hold simultaneously:
    //
    //   1. `<spacer h-24 sm:h-0 shrink-0>` — locked top safe zone on
    //      mobile (96px = 64px nav `h-16` + 32px breathing). Cannot
    //      collapse. Collapses to 0 on sm: and up since desktop nav is
    //      80px and desktop hero is tall enough that we don't need a
    //      reserved floor.
    //
    //   2. `<filler flex-1 min-h-0>` — flexible breathing band. Absorbs
    //      remaining space so the content sits at the BOTTOM of the
    //      hero (editorial cover feel restored). `min-h-0` lets it
    //      collapse to 0 first under pressure.
    //
    //   3. `<content shrink-0 pb-10 sm:pb-20 lg:pb-24>` — bottom-anchored
    //      content block. Natural height, tail padding for Safari UI /
    //      next-section clearance.
    //
    // Overflow behavior: when content + locked spacer + tail > hero
    // height (only on freakishly short viewports), the filler collapses
    // first, then any further overflow extends DOWNWARD past the hero
    // bottom (clipped cleanly by `overflow-hidden`) — never upward into
    // the nav. Nav clearance is GUARANTEED at every viewport because
    // the top spacer is `shrink-0`.
    //
    // `align="center"` (utility pages like /inquire) keeps its simple
    // centered layout — no top spacer, no flex layering needed.

    return (
        <section
            className={`relative w-full overflow-hidden bg-[hsl(var(--lux-ink))] ${heightClasses}`}
            data-testid={dataTestId}
        >
            {imageSrc ? (
                <img
                    key={imageSrc}
                    src={imageSrc}
                    srcSet={media?.srcSet || undefined}
                    sizes="100vw"
                    alt={media?.alt || ariaLabel}
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ objectPosition }}
                    data-testid={`${dataTestId}-image`}
                />
            ) : (
                <video
                    key={src}
                    src={src}
                    poster={poster}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label={media?.alt || ariaLabel}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ objectPosition }}
                    data-testid={`${dataTestId}-video`}
                />
            )}
            {/* Warm directional scrim — left-to-right, mirrors /business */}
            <div
                className="absolute inset-0"
                aria-hidden
                style={{
                    background:
                        "linear-gradient(90deg, rgba(18,16,14,0.62) 0%, rgba(18,16,14,0.42) 28%, rgba(18,16,14,0.28) 52%, rgba(18,16,14,0.16) 78%, rgba(18,16,14,0.10) 100%)",
                }}
            />
            {/* Bottom floor — keeps CTAs and subhead readable */}
            <div
                className="absolute inset-x-0 bottom-0 h-[45%]"
                aria-hidden
                style={{
                    background:
                        "linear-gradient(to top, rgba(18,16,14,0.38) 0%, rgba(18,16,14,0.18) 55%, rgba(18,16,14,0) 100%)",
                }}
            />
            {align === "center" ? (
                // Utility / center-aligned heroes — simple centered layout
                <div className="relative z-10 h-full flex flex-col justify-center">
                    <div
                        className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-10"
                        style={{ color: "#fbf8f4" }}
                    >
                        {children}
                    </div>
                </div>
            ) : (
                // Editorial bottom-anchored hero with hard nav-clearance floor
                <div className="relative z-10 h-full flex flex-col">
                    {/* Locked top safe zone — mobile only, never collapses */}
                    <div className="h-24 sm:h-0 shrink-0" aria-hidden="true" />
                    {/* Flexible breathing band — pushes content to the bottom */}
                    <div className="flex-1 min-h-0" aria-hidden="true" />
                    {/* Bottom-anchored content block */}
                    <div className="shrink-0 pb-10 sm:pb-20 lg:pb-24">
                        <div
                            className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-10"
                            style={{ color: "#fbf8f4" }}
                        >
                            {children}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

/**
 * Shared overlay text-shadow style. Apply to kicker, headline, subhead so
 * text always reads cleanly against the moving footage.
 */
export const heroTextShadow = {
    light: { textShadow: "0 1px 2px rgba(0,0,0,0.55)" },
    medium: { textShadow: "0 1px 2px rgba(0,0,0,0.5)" },
    heavy: {
        textShadow:
            "0 2px 18px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.4)",
    },
};

export default HeroVideo;
